import { createRoot, hydrateRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
// Critical font subset for the marketing landing (also reused by every
// authenticated route). Importing here makes Vite emit hashed woff2 +
// hashed CSS under /assets/ — the path on which Cloudflare actually
// applies our one-year Cache-Control TTL.
import './styles/fonts-marketing.css';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { HelmetProvider } from 'react-helmet-async';

const isDev = import.meta.env.DEV;
(() => {
  try {
    const stored = localStorage.getItem('guiri.theme');
    const resolved =
      stored === 'light' || stored === 'dark'
        ? stored
        : window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
    document.documentElement.setAttribute('data-theme', resolved);
  } catch {
    // no-op
  }
})();

// Register Service Worker for PWA (production only — avoids dev cache masking /assets/)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        if (isDev) console.log('Service Worker registered successfully:', registration.scope);
      })
      .catch((error) => {
        if (isDev) console.log('Service Worker registration failed:', error);
      });
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const tree = (
  <I18nextProvider i18n={i18n}>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </I18nextProvider>
);

// Only hydrate on `/`, the one route we prerender — every other path is
// served the same prerendered marketing HTML by the SPA fallback, so the
// React tree the client wants to render won't match. Wipe and mount fresh
// there. Dev (`vite dev`) also has no prerendered content and falls through
// to createRoot.
const shouldHydrate =
  rootElement.hasChildNodes() && window.location.pathname === '/';
if (shouldHydrate) {
  hydrateRoot(rootElement, tree);
} else {
  rootElement.innerHTML = '';
  createRoot(rootElement).render(tree);
}
